import * as React from "react";
import { Box, Button, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { ActiveRallyVideo } from "./ActiveRallyVideo";
import type { FirefightClip } from "./GameStory_firefights";
import type {
  PlayerIndexMap,
  RenderPlayerIdentity,
} from "./GameStoryDialog_UPDATED";
import type { KitchenArrivalRow, ShotNumberOverlay } from "./RallyBoardPopup";
import type { CvJson } from "../../shared/types/cvJson";

type ServingFilter = "all" | "team0" | "team1";
type PlayerId = 0 | 1 | 2 | 3;
type PositioningEvent = {
  key: string;
  label: string;
  shotNumber: number;
};

const PLAYER_IDS: PlayerId[] = [0, 1, 2, 3];
const ESTABLISHED_THRESHOLD = 0.75;
const LOW_THRESHOLD = 0.45;
const LAG_GAP_THRESHOLD = 0.35;
const COLLAPSE_DROP_THRESHOLD = 0.35;

function teamOfPlayerId(playerId: number): 0 | 1 {
  return playerId < 2 ? 0 : 1;
}

function getAdvantageValues(ov: ShotNumberOverlay | null | undefined) {
  const raw = (ov as any)?.advantage_scale ?? (ov as any)?.rawShot?.advantage_scale;
  if (!Array.isArray(raw) || raw.length < 4) return null;
  return raw.slice(0, 4).map((v: unknown) =>
    typeof v === "number" && Number.isFinite(v) ? v : null,
  ) as Array<number | null>;
}

function getPlayerSeries(row: KitchenArrivalRow, playerId: PlayerId): number[] {
  return (row.shotNumberOverlays ?? [])
    .map((ov) => getAdvantageValues(ov)?.[playerId] ?? null)
    .filter((v): v is number => typeof v === "number");
}

function firstCrossingShot(series: number[], threshold: number): number | null {
  for (let i = 0; i < series.length; i += 1) {
    if (series[i] >= threshold) return i + 1;
  }
  return null;
}

function maxDrop(series: number[]): number {
  let drop = 0;
  for (let i = 1; i < series.length; i += 1) {
    drop = Math.max(drop, series[i - 1] - series[i]);
  }
  return drop;
}

function average(series: number[]): number | null {
  if (!series.length) return null;
  return series.reduce((sum, v) => sum + v, 0) / series.length;
}

function getTeamPlayers(team: 0 | 1): [PlayerId, PlayerId] {
  return team === 0 ? [0, 1] : [2, 3];
}

function getSplitShotCount(row: KitchenArrivalRow, team: 0 | 1): number {
  const players = getTeamPlayers(team);
  let count = 0;
  for (const ov of row.shotNumberOverlays ?? []) {
    const values = getAdvantageValues(ov);
    if (!values) continue;
    const a = values[players[0]];
    const b = values[players[1]];
    if (a == null || b == null) continue;
    const split =
      (a >= ESTABLISHED_THRESHOLD && b < LOW_THRESHOLD) ||
      (b >= ESTABLISHED_THRESHOLD && a < LOW_THRESHOLD);
    if (split) count += 1;
  }
  return count;
}

function getTeamEstablishedShot(row: KitchenArrivalRow, team: 0 | 1): number | null {
  const players = getTeamPlayers(team);
  const overlays = row.shotNumberOverlays ?? [];
  for (let i = 0; i < overlays.length; i += 1) {
    const values = getAdvantageValues(overlays[i]);
    if (!values) continue;
    const a = values[players[0]];
    const b = values[players[1]];
    if (a != null && b != null && a >= ESTABLISHED_THRESHOLD && b >= ESTABLISHED_THRESHOLD) {
      return i + 1;
    }
  }
  return null;
}

function getShotOverlayByNumber(
  row: KitchenArrivalRow,
  shotNumber: number | null | undefined,
): ShotNumberOverlay | null {
  if (shotNumber == null) return null;
  return (
    (row.shotNumberOverlays ?? []).find((ov) => ov.shotNumber === shotNumber) ??
    null
  );
}

function getBiggestChangeShot(row: KitchenArrivalRow, playerId: PlayerId): number | null {
  const series = getPlayerSeries(row, playerId);
  if (series.length < 2) return null;
  let bestIdx = 1;
  let bestDelta = 0;
  for (let i = 1; i < series.length; i += 1) {
    const d = Math.abs(series[i] - series[i - 1]);
    if (d > bestDelta) {
      bestDelta = d;
      bestIdx = i;
    }
  }
  return bestDelta > 0 ? bestIdx + 1 : null;
}

function getTerminalOverlay(row: KitchenArrivalRow): ShotNumberOverlay | null {
  const overlays = row.shotNumberOverlays ?? [];
  return overlays.length ? overlays[overlays.length - 1] : null;
}

function getPartnerId(playerId: PlayerId): PlayerId {
  if (playerId === 0) return 1;
  if (playerId === 1) return 0;
  if (playerId === 2) return 3;
  return 2;
}

function isPlayerLaggingInValues(
  values: Array<number | null>,
  playerId: PlayerId,
): boolean {
  const playerValue = values[playerId];
  const partnerValue = values[getPartnerId(playerId)];
  if (playerValue == null || partnerValue == null) return false;
  return (
    partnerValue >= ESTABLISHED_THRESHOLD &&
    partnerValue - playerValue >= LAG_GAP_THRESHOLD
  );
}

function getLagShotNumbers(row: KitchenArrivalRow, playerId: PlayerId): number[] {
  const out: number[] = [];
  for (const ov of row.shotNumberOverlays ?? []) {
    const values = getAdvantageValues(ov);
    if (!values) continue;
    if (isPlayerLaggingInValues(values, playerId)) {
      out.push(ov.shotNumber);
    }
  }
  return out;
}

function getLaggingPlayers(row: KitchenArrivalRow): PlayerId[] {
  const out = new Set<PlayerId>();
  for (const ov of row.shotNumberOverlays ?? []) {
    const values = getAdvantageValues(ov);
    if (!values) continue;
    for (const playerId of PLAYER_IDS) {
      if (isPlayerLaggingInValues(values, playerId)) out.add(playerId);
    }
  }
  return Array.from(out);
}

function groupConsecutiveShots(shotNumbers: number[]): Array<{ start: number; end: number }> {
  const unique = shotNumbers
    .filter((n, idx, all) => Number.isFinite(n) && all.indexOf(n) === idx)
    .sort((a, b) => a - b);
  const groups: Array<{ start: number; end: number }> = [];
  for (const n of unique) {
    const last = groups[groups.length - 1];
    if (last && n === last.end + 1) {
      last.end = n;
    } else {
      groups.push({ start: n, end: n });
    }
  }
  return groups;
}

function formatShotRange(range: { start: number; end: number }): string {
  return range.start === range.end ? `S${range.start}` : `S${range.start}-${range.end}`;
}

function didTeamGetForcedBack(row: KitchenArrivalRow, team: 0 | 1): boolean {
  const players: [PlayerId, PlayerId] = team === 0 ? [0, 1] : [2, 3];
  let established = false;
  for (const ov of row.shotNumberOverlays ?? []) {
    const values = getAdvantageValues(ov);
    if (!values) continue;
    const a = values[players[0]];
    const b = values[players[1]];
    if (a == null || b == null) continue;
    if (a >= ESTABLISHED_THRESHOLD && b >= ESTABLISHED_THRESHOLD) {
      established = true;
    }
    if (established && (a < LOW_THRESHOLD || b < LOW_THRESHOLD)) {
      return true;
    }
  }
  return false;
}

function getTeamForcedBackShot(row: KitchenArrivalRow, team: 0 | 1): number | null {
  const players = getTeamPlayers(team);
  let established = false;
  for (const ov of row.shotNumberOverlays ?? []) {
    const values = getAdvantageValues(ov);
    if (!values) continue;
    const a = values[players[0]];
    const b = values[players[1]];
    if (a == null || b == null) continue;
    if (a >= ESTABLISHED_THRESHOLD && b >= ESTABLISHED_THRESHOLD) {
      established = true;
    } else if (established && (a < LOW_THRESHOLD || b < LOW_THRESHOLD)) {
      return ov.shotNumber;
    }
  }
  return null;
}

function getCollapsePlayers(row: KitchenArrivalRow): PlayerId[] {
  return PLAYER_IDS.filter((playerId) => {
    const series = getPlayerSeries(row, playerId);
    return maxDrop(series) >= COLLAPSE_DROP_THRESHOLD;
  });
}

function getPositioningEvents(row: KitchenArrivalRow): PositioningEvent[] {
  const events: PositioningEvent[] = [];

  for (const playerId of PLAYER_IDS) {
    for (const range of groupConsecutiveShots(getLagShotNumbers(row, playerId))) {
      events.push({
        key: `lag-${playerId}-${range.start}`,
        label: `lag p${playerId} ${formatShotRange(range)}`,
        shotNumber: range.start,
      });
    }
  }

  for (const team of [0, 1] as const) {
    const establishedShot = getTeamEstablishedShot(row, team);
    if (establishedShot != null) {
      events.push({
        key: `set-${team}-${establishedShot}`,
        label: `T${team} set S${establishedShot}`,
        shotNumber: establishedShot,
      });
    }

    const forcedBackShot = getTeamForcedBackShot(row, team);
    if (forcedBackShot != null) {
      events.push({
        key: `back-${team}-${forcedBackShot}`,
        label: `T${team} back S${forcedBackShot}`,
        shotNumber: forcedBackShot,
      });
    }
  }

  return events
    .sort((a, b) => a.shotNumber - b.shotNumber)
    .slice(0, 4);
}

function getRallyFlags(row: KitchenArrivalRow): string[] {
  const flags: string[] = [];
  const laggers = getLaggingPlayers(row);
  const collapsed = getCollapsePlayers(row);
  const forcedBackTeams = ([0, 1] as const).filter((team) =>
    didTeamGetForcedBack(row, team),
  );

  if (laggers.length) flags.push(`lag: ${laggers.map((p) => `p${p}`).join(", ")}`);
  if (forcedBackTeams.length) {
    flags.push(`forced back: ${forcedBackTeams.map((t) => `T${t}`).join(", ")}`);
  }
  if (collapsed.length) {
    flags.push(`drop: ${collapsed.map((p) => `p${p}`).join(", ")}`);
  }

  const team0Split = getSplitShotCount(row, 0);
  const team1Split = getSplitShotCount(row, 1);
  if (team0Split >= 3) flags.push(`split T0 ${team0Split}`);
  if (team1Split >= 3) flags.push(`split T1 ${team1Split}`);

  return flags.slice(0, 3);
}

function TeamHalfState(props: {
  row: KitchenArrivalRow;
  team: 0 | 1;
  shotNumber: number | null;
  renderPlayerIdentity: RenderPlayerIdentity;
}) {
  const { row, team, shotNumber, renderPlayerIdentity } = props;
  const winningTeam = row.winnerTeam === team;
  const teamColor =
    team === 0 ? "rgba(90,165,255,0.95)" : "rgba(90,220,140,0.95)";
  const players = getTeamPlayers(team);
  const overlay =
    getShotOverlayByNumber(row, shotNumber) ??
    getShotOverlayByNumber(row, 1);
  const values = getAdvantageValues(overlay);
  const shotLabel = overlay?.shotNumber == null ? "--" : `S${overlay.shotNumber}`;

  return (
    <Box
      sx={{
        px: 0.45,
        py: 0.35,
        borderRadius: 0.75,
        bgcolor: winningTeam ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.025)",
        border: winningTeam
          ? `1px solid ${teamColor}`
          : "1px solid rgba(255,255,255,0.08)",
        boxShadow: winningTeam
          ? `inset 0 0 0 1px ${team === 0 ? "rgba(90,165,255,0.18)" : "rgba(90,220,140,0.18)"}`
          : "none",
        minHeight: 42,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 0.15,
          fontSize: 9.5,
          color: "rgba(255,255,255,0.52)",
          lineHeight: 1,
        }}
      >
        <Box>{team === 0 ? "base -> NVZ" : "NVZ <- base"}</Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.35 }}>
          {winningTeam ? (
            <Box
              sx={{
                px: 0.35,
                py: 0.02,
                borderRadius: 999,
                bgcolor: teamColor,
                color: "rgba(0,0,0,0.82)",
                fontSize: 8.5,
                fontWeight: 950,
                lineHeight: 1.05,
              }}
            >
              W
            </Box>
          ) : null}
          <Box>{shotLabel}</Box>
        </Box>
      </Box>
      {players.map((playerId) => {
        const value = values?.[playerId] ?? null;
        const clamped =
          typeof value === "number" ? Math.max(0, Math.min(1, value)) : null;
        const leftPct =
          clamped == null ? 50 : (team === 0 ? clamped : 1 - clamped) * 100;
        const lagging = values ? isPlayerLaggingInValues(values, playerId) : false;
        const avatarEl = (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.2 }}>
            {renderPlayerIdentity({ playerId, size: 18, fontSize: 7 })}
          </Box>
        );
        const valueEl = (
          <Box
            sx={{
              fontSize: 9.5,
              fontWeight: 800,
              color: lagging
                ? "rgba(255,210,120,0.98)"
                : "rgba(255,255,255,0.58)",
              fontVariantNumeric: "tabular-nums",
              textAlign: team === 0 ? "right" : "left",
            }}
          >
            {value == null ? "--" : value.toFixed(2)}
          </Box>
        );
        return (
          <Box
            key={playerId}
            sx={{
              display: "grid",
              gridTemplateColumns: team === 0 ? "22px 1fr 28px" : "28px 1fr 22px",
              alignItems: "center",
              gap: 0.35,
              mb: 0.1,
              minWidth: 0,
            }}
          >
            {team === 0 ? avatarEl : valueEl}
            <Box
              sx={{
                position: "relative",
                height: 10,
                borderRadius: 999,
                bgcolor: "rgba(255,255,255,0.08)",
                overflow: "visible",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: 4,
                  left: 0,
                  right: 0,
                  height: 1,
                  bgcolor: "rgba(255,255,255,0.18)",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  left: `${leftPct}%`,
                  top: "50%",
                  width: lagging ? 10 : 8,
                  height: lagging ? 10 : 8,
                  transform: "translate(-50%, -50%)",
                  borderRadius: "50%",
                  bgcolor:
                    lagging
                      ? "rgba(255,190,85,0.98)"
                      : team === 0
                        ? "rgba(90,165,255,0.98)"
                        : "rgba(90,220,140,0.98)",
                  border: "1px solid rgba(255,255,255,0.82)",
                  boxShadow: lagging
                    ? "0 0 0 2px rgba(255,190,85,0.22)"
                    : "0 1px 4px rgba(0,0,0,0.35)",
                }}
              />
            </Box>
            {team === 0 ? valueEl : avatarEl}
          </Box>
        );
      })}
    </Box>
  );
}

function TeamSparkline(props: {
  row: KitchenArrivalRow;
  selected?: boolean;
  team: 0 | 1;
  currentShotNumber?: number | null;
  onHoverShotNumber?: (shotNumber: number | null) => void;
}) {
  const {
    row,
    selected,
    team,
    currentShotNumber,
    onHoverShotNumber,
  } = props;
  const [hoverIdx, setHoverIdx] = React.useState<number | null>(null);
  const players = getTeamPlayers(team);
  const seriesA = getPlayerSeries(row, players[0]);
  const seriesB = getPlayerSeries(row, players[1]);
  const shotCount = Math.max(seriesA.length, seriesB.length);
  const winningTeam = row.winnerTeam === team;
  const color = team === 0 ? "rgba(90,165,255,0.98)" : "rgba(90,220,140,0.98)";
  const colorB = team === 0 ? "rgba(155,205,255,0.92)" : "rgba(150,245,185,0.92)";
  const fill = team === 0 ? "rgba(90,165,255,0.16)" : "rgba(90,220,140,0.16)";

  if (!shotCount) {
    return <Box sx={{ fontSize: 11, color: "rgba(255,255,255,0.38)" }}>no data</Box>;
  }

  const w = 230;
  const h = 58;
  const pad = 6;
  const plotW = w - pad * 2;
  const plotH = h - pad * 2;
  const xOf = (idx: number) =>
    shotCount <= 1 ? w / 2 : pad + (idx / (shotCount - 1)) * plotW;
  const xOfFloat = (idx: number) =>
    shotCount <= 1 ? w / 2 : pad + (idx / (shotCount - 1)) * plotW;
  const yOf = (v: number) => pad + (1 - Math.max(0, Math.min(1, v))) * plotH;
  const pointsOf = (series: number[]) =>
    series.map((v, i) => `${xOf(i)},${yOf(v)}`).join(" ");
  const area = `${pad},${h - pad} ${seriesA
    .map((v, i) => `${xOf(i)},${yOf(v)}`)
    .join(" ")} ${w - pad},${h - pad}`;
  const currentIdx =
    currentShotNumber == null
      ? null
      : Math.max(0, Math.min(shotCount - 1, currentShotNumber - 1));
  const lagShotNumbers = [
    ...getLagShotNumbers(row, players[0]),
    ...getLagShotNumbers(row, players[1]),
  ];
  const lagIdxs = lagShotNumbers
    .map((shotNumber) => Math.max(0, Math.min(shotCount - 1, shotNumber - 1)))
    .filter((idx, idxIndex, all) => all.indexOf(idx) === idxIndex);
  const valueAt = (series: number[], idx: number | null) =>
    idx == null || series[idx] == null ? null : series[idx];
  const valueAtFloat = (series: number[], idxFloat: number): number | null => {
    if (!series.length) return null;
    if (idxFloat < 0 || idxFloat > series.length - 1) return null;
    const lo = Math.floor(idxFloat);
    const hi = Math.ceil(idxFloat);
    const loValue = series[lo];
    const hiValue = series[hi];
    if (loValue == null || hiValue == null) return null;
    if (lo === hi) return loValue;
    const t = idxFloat - lo;
    return loValue + (hiValue - loValue) * t;
  };
  const lagGapPolygons = groupConsecutiveShots(lagIdxs.map((idx) => idx + 1))
    .map((range) => {
      const startIdx = range.start - 1;
      const endIdx = range.end - 1;
      const startFloat = Math.max(0, startIdx - 0.45);
      const endFloat = Math.min(shotCount - 1, endIdx + 0.45);
      const idxs = [startFloat];
      for (let idx = Math.ceil(startFloat); idx <= Math.floor(endFloat); idx += 1) {
        if (idx > startFloat && idx < endFloat) idxs.push(idx);
      }
      if (endFloat !== startFloat) idxs.push(endFloat);

      const topPoints: string[] = [];
      const bottomPoints: string[] = [];
      for (const idx of idxs) {
        const a = valueAtFloat(seriesA, idx);
        const b = valueAtFloat(seriesB, idx);
        if (a == null || b == null) continue;
        topPoints.push(`${xOfFloat(idx)},${Math.min(yOf(a), yOf(b))}`);
        bottomPoints.push(`${xOfFloat(idx)},${Math.max(yOf(a), yOf(b))}`);
      }
      if (!topPoints.length || !bottomPoints.length) return null;

      return `${topPoints.join(" ")} ${bottomPoints.reverse().join(" ")}`;
    })
    .filter((points): points is string => !!points);

  return (
    <>
      <svg
        viewBox={`0 0 ${w} ${h}`}
        preserveAspectRatio="none"
        onMouseMove={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const ratio =
            rect.width <= 0
              ? 0
              : Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
          const idx = shotCount <= 1 ? 0 : Math.round(ratio * (shotCount - 1));
          setHoverIdx(idx);
          onHoverShotNumber?.(idx + 1);
        }}
        onMouseLeave={() => {
          setHoverIdx(null);
          onHoverShotNumber?.(null);
        }}
        style={{
          width: "100%",
          height: 62,
          display: "block",
          background: winningTeam
            ? team === 0
              ? "rgba(90,165,255,0.075)"
              : "rgba(90,220,140,0.075)"
            : selected
              ? "rgba(255,255,255,0.07)"
              : "rgba(255,255,255,0.03)",
          border: winningTeam
            ? `1px solid ${team === 0 ? "rgba(90,165,255,0.36)" : "rgba(90,220,140,0.36)"}`
            : "1px solid transparent",
          borderRadius: 4,
        }}
      >
        <line
          x1={0}
          x2={w}
          y1={yOf(ESTABLISHED_THRESHOLD)}
          y2={yOf(ESTABLISHED_THRESHOLD)}
          stroke="rgba(255,255,255,0.16)"
          strokeDasharray="3 3"
          strokeWidth={1}
        />
        <polygon points={area} fill={fill} />
        {lagGapPolygons.map((points, idx) => (
          <polygon
            key={`lag-gap-${idx}`}
            points={points}
            fill="rgba(255,190,85,0.28)"
            stroke="rgba(255,190,85,0.62)"
            strokeWidth={0.75}
          />
        ))}
        <polyline
          points={pointsOf(seriesA)}
          fill="none"
          stroke={color}
          strokeWidth={selected ? 3 : 2.2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <polyline
          points={pointsOf(seriesB)}
          fill="none"
          stroke={colorB}
          strokeWidth={selected ? 2.6 : 1.9}
          strokeDasharray="5 3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {hoverIdx != null ? (
          <line
            x1={xOf(hoverIdx)}
            x2={xOf(hoverIdx)}
            y1={0}
            y2={h}
            stroke="rgba(255,255,255,0.30)"
            strokeWidth={0.8}
          />
        ) : null}
        {players.map((playerId, playerIdx) => {
          const series = playerIdx === 0 ? seriesA : seriesB;
          const value = valueAt(series, currentIdx);
          if (currentIdx == null || value == null) return null;
          return (
            <circle
              key={playerId}
              cx={xOf(currentIdx)}
              cy={yOf(value)}
              r={playerIdx === 0 ? 4 : 3.5}
              fill="rgba(255,255,255,0.98)"
              stroke="rgba(0,0,0,0.70)"
              strokeWidth={1.2}
            />
          );
        })}
      </svg>
    </>
  );
}

function formatMaybeShot(n: number | null): string {
  return n == null ? "--" : `S${n}`;
}

function formatPct(n: number | null): string {
  return n == null ? "--" : n.toFixed(2);
}

export function PositioningPatternsPopup(props: {
  open: boolean;
  onClose: () => void;
  packageId: string;
  rows: KitchenArrivalRow[];
  cvJson: CvJson | null | undefined;
  renderPlayerIdentity: RenderPlayerIdentity;
  insightsToCvPlayerIndex: PlayerIndexMap;
  firefightsClips: FirefightClip[];
  firefightRallySet: Set<number>;
}) {
  const {
    open,
    onClose,
    packageId,
    rows,
    cvJson,
    renderPlayerIdentity,
    insightsToCvPlayerIndex,
    firefightsClips,
    firefightRallySet,
  } = props;

  const [servingFilter, setServingFilter] = React.useState<ServingFilter>("all");
  const [selectedPlayerId, setSelectedPlayerId] = React.useState<PlayerId>(0);
  const [selectedRallyIndex, setSelectedRallyIndex] = React.useState<number | null>(null);
  const [playbackShotNumber, setPlaybackShotNumber] = React.useState<
    number | null
  >(null);
  const [hoveredShotState, setHoveredShotState] = React.useState<{
    rallyIndex: number;
    shotNumber: number;
  } | null>(null);
  const [pinnedShotState, setPinnedShotState] = React.useState<{
    rallyIndex: number;
    shotNumber: number;
  } | null>(null);

  const filteredRows = React.useMemo(() => {
    return rows.filter((row) => {
      if (!row.shotNumberOverlays?.some((ov) => getAdvantageValues(ov))) return false;
      if (servingFilter === "team0") return row.servingTeam === 0;
      if (servingFilter === "team1") return row.servingTeam === 1;
      return true;
    });
  }, [rows, servingFilter]);

  React.useEffect(() => {
    if (!open) return;
    setSelectedRallyIndex((prev) => {
      if (prev != null && filteredRows.some((r) => r.rallyIndex === prev)) return prev;
      return filteredRows[0]?.rallyIndex ?? null;
    });
  }, [filteredRows, open]);

  React.useEffect(() => {
    setPlaybackShotNumber(null);
    setHoveredShotState(null);
    setPinnedShotState(null);
  }, [selectedRallyIndex]);

  if (!open) return null;

  const selectedRow =
    filteredRows.find((r) => r.rallyIndex === selectedRallyIndex) ??
    filteredRows[0] ??
    null;
  const selectedSeries = selectedRow
    ? getPlayerSeries(selectedRow, selectedPlayerId)
    : [];
  const selectedCrossing = firstCrossingShot(selectedSeries, ESTABLISHED_THRESHOLD);
  const selectedChangeShot = selectedRow
    ? getBiggestChangeShot(selectedRow, selectedPlayerId)
    : null;
  const selectedRallyFlags = selectedRow ? getRallyFlags(selectedRow) : [];

  const summaryRows = PLAYER_IDS.map((playerId) => {
    const seriesByRally = filteredRows.map((row) => getPlayerSeries(row, playerId));
    const crossings = seriesByRally.map((s) => firstCrossingShot(s, ESTABLISHED_THRESHOLD));
    const crossed = crossings.filter((n): n is number => n != null);
    const avgCross = crossed.length
      ? crossed.reduce((sum, n) => sum + n, 0) / crossed.length
      : null;
    const avgValue = average(seriesByRally.flat());
    const drops = seriesByRally.map(maxDrop);
    const biggestDrop = drops.length ? Math.max(...drops) : 0;

    return {
      playerId,
      avgCross,
      crossedCount: crossed.length,
      avgValue,
      biggestDrop,
    };
  });

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 16,
        zIndex: 4300,
        bgcolor: "rgba(14,14,14,0.98)",
        color: "rgba(255,255,255,0.92)",
        border: "1px solid rgba(255,255,255,0.18)",
        borderRadius: 1,
        boxShadow: "0 18px 70px rgba(0,0,0,0.48)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 1.35,
          py: 0.85,
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderBottom: "1px solid rgba(255,255,255,0.12)",
        }}
      >
        <Box sx={{ fontSize: 18, fontWeight: 900 }}>Positioning Patterns</Box>
        <Box sx={{ fontSize: 12.5, color: "rgba(255,255,255,0.62)" }}>
          Sparklines show advantage_scale across shots. Higher means closer to
          the NVZ line. Yellow = first 0.75 cross, white = current shot, red =
          terminal hitter.
        </Box>
        <Box sx={{ flex: 1 }} />
        <IconButton size="small" onClick={onClose} sx={{ color: "rgba(255,255,255,0.72)" }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box
        sx={{
          px: 1.35,
          py: 0.7,
          borderBottom: "1px solid rgba(255,255,255,0.10)",
          bgcolor: "rgba(255,255,255,0.035)",
          fontSize: 12.5,
          lineHeight: 1.35,
          color: "rgba(255,255,255,0.74)",
        }}
      >
        <Box sx={{ fontWeight: 900, color: "rgba(255,255,255,0.9)", mb: 0.25 }}>
          Experiment note
        </Box>
        The purpose of this experiment was to see if there is any insight to be gleaned from the player positioning with respect to how the game turned out. While kind of interesting to watch, there's too much decoding forced on the user and there wasn't any astounding insight that just jumped out.
      </Box>

      <Box
        sx={{
          px: 1,
          py: 0.75,
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderBottom: "1px solid rgba(255,255,255,0.10)",
        }}
      >
        <Box sx={{ fontSize: 12, fontWeight: 900 }}>Serving</Box>
        {[
          ["all", "All"],
          ["team0", "Team 0"],
          ["team1", "Team 1"],
        ].map(([key, label]) => (
          <Button
            key={key}
            size="small"
            variant={servingFilter === key ? "contained" : "outlined"}
            onClick={() => setServingFilter(key as ServingFilter)}
            sx={{ textTransform: "none", minHeight: 28, py: 0.1 }}
          >
            {label}
          </Button>
        ))}
        <Box sx={{ width: 10 }} />
        <Box sx={{ fontSize: 12, fontWeight: 900 }}>Focus player</Box>
        {PLAYER_IDS.map((playerId) => (
          <Button
            key={playerId}
            size="small"
            variant={selectedPlayerId === playerId ? "contained" : "outlined"}
            onClick={() => setSelectedPlayerId(playerId)}
            sx={{ textTransform: "none", minWidth: 62, minHeight: 30, py: 0.1 }}
          >
            p{playerId}
          </Button>
        ))}
        <Box sx={{ flex: 1 }} />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.9,
            fontSize: 11,
            color: "rgba(255,255,255,0.62)",
            whiteSpace: "nowrap",
          }}
        >
          <Box component="span" sx={{ color: "rgba(255,230,120,0.98)" }}>●</Box>
          first cross
          <Box component="span" sx={{ color: "rgba(255,255,255,0.98)" }}>●</Box>
          current
          <Box component="span" sx={{ color: "rgba(255,55,55,0.98)" }}>●</Box>
          last shot
        </Box>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, display: "flex" }}>
        <Box
          sx={{
            width: 910,
            minWidth: 910,
            display: "flex",
            flexDirection: "column",
            borderRight: "1px solid rgba(255,255,255,0.12)",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "74px minmax(220px, 1fr) 132px 132px minmax(220px, 1fr)",
              gap: 0.5,
              px: 0.9,
              py: 0.6,
              borderBottom: "1px solid rgba(255,255,255,0.10)",
              fontSize: 12,
              fontWeight: 900,
              color: "rgba(255,255,255,0.72)",
            }}
          >
            <Box>Rally</Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
              <Box>Team 0 history</Box>
              {renderPlayerIdentity({ playerId: 0, size: 20, fontSize: 8 })}
              {renderPlayerIdentity({ playerId: 1, size: 20, fontSize: 8 })}
            </Box>
            <Box sx={{ textAlign: "center" }}>Team 0</Box>
            <Box sx={{ textAlign: "center" }}>Team 1</Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
              <Box>Team 1 history</Box>
              {renderPlayerIdentity({ playerId: 2, size: 20, fontSize: 8 })}
              {renderPlayerIdentity({ playerId: 3, size: 20, fontSize: 8 })}
            </Box>
          </Box>

          <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 0.9, py: 0.7 }}>
            {filteredRows.map((row) => {
              const selected = selectedRow?.rallyIndex === row.rallyIndex;
              const rallyFlags = getRallyFlags(row);
              const positioningEvents = getPositioningEvents(row);
              const rowShotNumber =
                hoveredShotState?.rallyIndex === row.rallyIndex
                  ? hoveredShotState.shotNumber
                  : pinnedShotState?.rallyIndex === row.rallyIndex
                    ? pinnedShotState.shotNumber
                    : selected && playbackShotNumber != null
                      ? playbackShotNumber
                      : 1;
              const renderTeamSparklineCell = (team: 0 | 1) => (
                <Box
                  key={team}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedRallyIndex(row.rallyIndex);
                  }}
                >
                  <TeamSparkline
                    row={row}
                    team={team}
                    selected={selected}
                    currentShotNumber={rowShotNumber}
                    onHoverShotNumber={(shotNumber) => {
                      setHoveredShotState(
                        shotNumber == null
                          ? null
                          : { rallyIndex: row.rallyIndex, shotNumber },
                      );
                    }}
                  />
                </Box>
              );
              return (
                <Box
                  key={row.rallyIndex}
                  onClick={() => setSelectedRallyIndex(row.rallyIndex)}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "74px minmax(220px, 1fr) 132px 132px minmax(220px, 1fr)",
                    gap: 0.5,
                    alignItems: "center",
                    mb: 0.45,
                    p: 0.45,
                    borderRadius: 1,
                    cursor: "pointer",
                    bgcolor: selected ? "rgba(255,255,255,0.105)" : "transparent",
                    border: selected
                      ? "1px solid rgba(255,255,255,0.24)"
                      : "1px solid transparent",
                    borderLeft:
                      row.winnerTeam === 0
                        ? "4px solid rgba(90,165,255,0.95)"
                        : row.winnerTeam === 1
                          ? "4px solid rgba(90,220,140,0.95)"
                          : "4px solid transparent",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.07)" },
                  }}
                >
                  <Box sx={{ fontSize: 12, lineHeight: 1.2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.45 }}>
                      <Box sx={{ fontWeight: 900 }}>R{row.rallyIndex + 1}</Box>
                      {row.winnerTeam != null ? (
                        <Box
                          sx={{
                            px: 0.45,
                            py: 0.05,
                            borderRadius: 999,
                            fontSize: 9.5,
                            fontWeight: 900,
                            bgcolor:
                              row.winnerTeam === 0
                                ? "rgba(90,165,255,0.22)"
                                : "rgba(90,220,140,0.22)",
                            color:
                              row.winnerTeam === 0
                                ? "rgba(145,200,255,0.98)"
                                : "rgba(140,240,175,0.98)",
                            border: "1px solid rgba(255,255,255,0.12)",
                          }}
                        >
                          W{row.winnerTeam}
                        </Box>
                      ) : null}
                    </Box>
                    <Box sx={{ color: "rgba(255,255,255,0.52)" }}>
                      {row.servingTeam == null ? "S ?" : `S T${row.servingTeam}`}
                      {" • "}
                      {row.winnerTeam == null ? "W ?" : `W T${row.winnerTeam}`}
                    </Box>
                    {rallyFlags.length ? (
                      <Box
                        sx={{
                          mt: 0.25,
                          color: "rgba(255,210,120,0.92)",
                          fontSize: 9.5,
                          lineHeight: 1.2,
                        }}
                      >
                        {rallyFlags.join(" • ")}
                      </Box>
                    ) : null}
                    {positioningEvents.length ? (
                      <Box
                        sx={{
                          mt: 0.3,
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 0.25,
                        }}
                      >
                        {positioningEvents.map((event) => {
                          const pinned =
                            pinnedShotState?.rallyIndex === row.rallyIndex &&
                            pinnedShotState.shotNumber === event.shotNumber;
                          return (
                            <Box
                              key={event.key}
                              component="button"
                              type="button"
                              onMouseEnter={() =>
                                setHoveredShotState({
                                  rallyIndex: row.rallyIndex,
                                  shotNumber: event.shotNumber,
                                })
                              }
                              onMouseLeave={() => setHoveredShotState(null)}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedRallyIndex(row.rallyIndex);
                                setPinnedShotState({
                                  rallyIndex: row.rallyIndex,
                                  shotNumber: event.shotNumber,
                                });
                              }}
                              sx={{
                                px: 0.35,
                                py: 0.05,
                                borderRadius: 0.5,
                                border: pinned
                                  ? "1px solid rgba(255,220,135,0.95)"
                                  : "1px solid rgba(255,190,85,0.36)",
                                bgcolor: pinned
                                  ? "rgba(255,190,85,0.22)"
                                  : "rgba(255,190,85,0.08)",
                                color: "rgba(255,220,135,0.96)",
                                fontSize: 8.5,
                                fontWeight: 850,
                                lineHeight: 1.1,
                                cursor: "pointer",
                                fontFamily: "inherit",
                              }}
                            >
                              {event.label}
                            </Box>
                          );
                        })}
                      </Box>
                    ) : null}
                  </Box>
                  {renderTeamSparklineCell(0)}
                  <TeamHalfState
                    row={row}
                    team={0}
                    shotNumber={rowShotNumber}
                    renderPlayerIdentity={renderPlayerIdentity}
                  />
                  <TeamHalfState
                    row={row}
                    team={1}
                    shotNumber={rowShotNumber}
                    renderPlayerIdentity={renderPlayerIdentity}
                  />
                  {renderTeamSparklineCell(1)}
                </Box>
              );
            })}
          </Box>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          <Box
            sx={{
              px: 1,
              py: 0.75,
              display: "grid",
              gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
              gap: 0.7,
              borderBottom: "1px solid rgba(255,255,255,0.10)",
            }}
          >
            {summaryRows.map((s) => (
              <Box
                key={s.playerId}
                sx={{
                  p: 0.75,
                  borderRadius: 1,
                  bgcolor:
                    s.playerId === selectedPlayerId
                      ? "rgba(255,255,255,0.10)"
                      : "rgba(255,255,255,0.045)",
                  border: "1px solid rgba(255,255,255,0.10)",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.35 }}>
                  {renderPlayerIdentity({ playerId: s.playerId, size: 26, fontSize: 9 })}
                  <Box sx={{ fontSize: 12, fontWeight: 900 }}>p{s.playerId}</Box>
                </Box>
                <Box sx={{ fontSize: 11, color: "rgba(255,255,255,0.62)", lineHeight: 1.45 }}>
                  Avg cross: {formatPct(s.avgCross)}
                  <br />
                  Crossed: {s.crossedCount}/{filteredRows.length}
                  <br />
                  Avg value: {formatPct(s.avgValue)}
                  <br />
                  Max drop: {formatPct(s.biggestDrop)}
                </Box>
              </Box>
            ))}
          </Box>

          <Box sx={{ px: 1, py: 0.7, borderBottom: "1px solid rgba(255,255,255,0.10)" }}>
            <Box sx={{ fontSize: 14, fontWeight: 900 }}>
              {selectedRow
                ? `Rally ${selectedRow.rallyIndex + 1} • p${selectedPlayerId}`
                : "No rally selected"}
            </Box>
            <Box sx={{ mt: 0.25, fontSize: 12, color: "rgba(255,255,255,0.62)" }}>
              First crossed {ESTABLISHED_THRESHOLD.toFixed(2)}:{" "}
              {formatMaybeShot(selectedCrossing)} • Biggest change:{" "}
              {formatMaybeShot(selectedChangeShot)} • Team 0 split shots:{" "}
              {selectedRow ? getSplitShotCount(selectedRow, 0) : "--"} • Team 1
              split shots: {selectedRow ? getSplitShotCount(selectedRow, 1) : "--"} •
              Team 0 established:{" "}
              {selectedRow ? formatMaybeShot(getTeamEstablishedShot(selectedRow, 0)) : "--"} •
              Team 1 established:{" "}
              {selectedRow ? formatMaybeShot(getTeamEstablishedShot(selectedRow, 1)) : "--"}
              {selectedRallyFlags.length ? (
                <>
                  {" "}
                  • Flags: {selectedRallyFlags.join(" • ")}
                </>
              ) : null}
            </Box>
          </Box>

          <Box sx={{ flex: 1, minHeight: 0, bgcolor: "black" }}>
            {selectedRow ? (
              <ActiveRallyVideo
                aiOpenViaParent={false}
                insightsToCvPlayerIndex={insightsToCvPlayerIndex}
                renderPlayerIdentity={renderPlayerIdentity}
                aiContextLabel="Positioning Patterns"
                focusedFilter={null}
                packageId={packageId}
                row={selectedRow}
                rows={rows}
                activeShotNumber={null}
                activeShotExtraText={undefined}
                shotOverlayPersistentExtraText={undefined}
                showCondensedTimeline={false}
                fontColor="white"
                controlsPlacement="left"
                firefightsClips={firefightsClips}
                firefightRallySet={firefightRallySet}
                showRateControls={true}
                showTitle={true}
                showScoreEtc={true}
                showAi={true}
                aiDrawerMode="split"
                showLoopScrubber={true}
                defaultLoopUiOpen={false}
                showPopoutButton={true}
                isInFullPopup={false}
                cvJson={cvJson}
                showAdvantageScaleMarkers={true}
                showAdvantageScaleSparklines={true}
                onPlaybackShotNumberChange={setPlaybackShotNumber}
              />
            ) : null}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
